import FormulaRowElement from 'models/data/formula/formula-row-element-model';
import FormulaRowNumber from 'models/data/formula/formula-row-number-model';
import {
  FORMULA_ROW_TYPES,
  COPY_SUCCESS,
  COPY_FAILED,
} from 'constants/feature/formula-constants';

//Calculate positions and gew new formula rows for the target element.
export const _getNewRowsForTargetElement = (
  srcElement,
  targetElement,
  copiedRows,
  tableMatrix,
) => {
  try {
    let firstNonUndefinedIndex = tableMatrix.findIndex(
      (el) => el !== undefined,
    );
    let isAtleastOneElementFound = false; // if no elements or numbers found, we should see the error.
    //loop through copied rows and change only elements with new positions and retain operators and numbers
    const newRows = copiedRows.map((row) => {
      // find the difference in row wise and column wise postions between source and target elements.
      let rowIndexDiff =
        tableMatrix[targetElement].row - tableMatrix[srcElement].row;
      let columnIndexDiff =
        tableMatrix[targetElement].column - tableMatrix[srcElement].column;
      switch (row.type) {
        case FORMULA_ROW_TYPES.ELEMENT: {
          let targetRowElement;
          let targetRowElementPos;
          try {
            let targetRowElementRowColumn = [
              tableMatrix[row.elementData.id].row + rowIndexDiff,
              tableMatrix[row.elementData.id].column + columnIndexDiff,
            ];
            targetRowElementPos =
              tableMatrix
                // Since the array can consist of lot of undefined values behind,
                // so I choose to start from first non-undefined index.
                // Pardon me for calling it 'non-undefined'.
                // I choose to call it that way since 'undefined' is a keyword here.
                .slice(firstNonUndefinedIndex)
                .findIndex(
                  (ele) =>
                    ele &&
                    ele.row === targetRowElementRowColumn[0] &&
                    ele.column === targetRowElementRowColumn[1],
                ) + firstNonUndefinedIndex;

            targetRowElement = tableMatrix[targetRowElementPos];
          } catch (e) {
            //incase if there is any exception while trying to get the targetRowElement, ignore it since we are validating targetRowElement below.
          }
          if (targetRowElement) {
            isAtleastOneElementFound = true;
            return new FormulaRowElement({
              elementData: {
                id: targetRowElementPos,
              },
            });
          } else {
            //if element not found , add 0 to the formula.
            return new FormulaRowNumber({
              value: 0,
            });
          }
        }
        case FORMULA_ROW_TYPES.NUMBER: {
          isAtleastOneElementFound = true;
          return row;
        }
        default:
          return row;
      }
    });
    return isAtleastOneElementFound ? newRows : null;
  } catch (e) {
    return null;
    //TODO: can set error in copyFormula state in the store.
  }
};

//get table from the selected element.
export const getCurrentTable = (elementId) => {
  // If ElementId is null, return null and don't initiate copy formula
  let tag =
    elementId &&
    document.getElementById(`CFTO_ELEMENT_${elementId}`) &&
    document.getElementById(`CFTO_ELEMENT_${elementId}`).parentNode;
  while (tag && tag.tagName !== 'TABLE') {
    tag = tag.parentNode;
  }
  return tag;
};

export const removeUnwantedColumn = (dataTable, maxColumnValue) => {
  let emptyColumns = [];
  let firstNonUndefinedIndex = dataTable.findIndex((ele) => ele !== undefined);
  for (let i = 0; i < maxColumnValue; i++) {
    if (dataTable.findIndex((ele) => ele && ele.column === i) === -1)
      emptyColumns.push(i);
  }
  dataTable.slice(firstNonUndefinedIndex).forEach((elementData) => {
    elementData &&
      (dataTable[elementData.elementId] = {
        elementId: elementData.elementId,
        value: elementData.value,
        row: elementData.row,
        column:
          elementData.column -
          emptyColumns.reduce(
            (prevValue, currentValue) =>
              currentValue < elementData.column ? prevValue + 1 : prevValue,
            0,
          ),
      });
  });
  return dataTable;
};

// OCR Polygon parsing and flattening
export const parseAndFlattenPolygon = (coordinatesInInches) => {
  if (!coordinatesInInches) return [];
  const parsed =
    typeof coordinatesInInches === 'string'
      ? (() => {
          try {
            return JSON.parse(coordinatesInInches);
          } catch {
            return [];
          }
        })()
      : coordinatesInInches;
  return Array.isArray(parsed)
    ? Array.isArray(parsed[0])
      ? parsed[0]
      : parsed
    : [];
};

// Calculate overlap area between two rectangles
export const getOverlapArea = (rect1, rect2) => {
  const w = Math.min(rect1.x3, rect2.x3) - Math.max(rect1.x1, rect2.x1);
  const h = Math.min(rect1.y3, rect2.y3) - Math.max(rect1.y1, rect2.y1);
  return w > 0 && h > 0 ? w * h : 0;
};

// Find cell position by calculating overlap with OCR table cells
export const findCellPositionByOverlap = (elementCoords, tableCells) => {
  if (!tableCells?.length || !elementCoords || elementCoords.length < 6)
    return null;

  const [ex1, ey1, , , ex3, ey3] = elementCoords;
  const elementRect = { x1: ex1, y1: ey1, x3: ex3, y3: ey3 };
  let maxOverlap = 0,
    matchedCell = null;

  tableCells.forEach((cell) => {
    const polygon =
      cell.bounding_regions?.[0]?.polygon || cell.boundingRegions?.[0]?.polygon;
    if (!polygon || polygon.length < 6) return;

    const [cx1, cy1, , , cx3, cy3] = polygon;
    const overlap = getOverlapArea(elementRect, {
      x1: cx1,
      y1: cy1,
      x3: cx3,
      y3: cy3,
    });
    if (overlap > maxOverlap) {
      maxOverlap = overlap;
      matchedCell = {
        row: cell.rowIndex ?? cell.row_index ?? -1,
        column: cell.columnIndex ?? cell.column_index ?? -1,
      };
    }
  });

  return matchedCell;
};

// Resolve element position using OCR metadata or cell overlap
export const resolveElementPosition = (entry, tableCells) => {
  let { rowNum: row, columnNum: column } = entry;
  if (row < 0 || column < 0) {
    const cellPosition = findCellPositionByOverlap(
      parseAndFlattenPolygon(entry.coordinatesInInches),
      tableCells,
    );
    if (cellPosition) {
      if (row < 0) row = cellPosition.row;
      if (column < 0) column = cellPosition.column;
    }
  }
  return { row, column };
};

// Build a table matrix entry
export const buildMatrixEntry = (entry, row, column) => ({
  elementId: entry.id.toString(),
  value: entry.display,
  row,
  column,
});

// Infer element positions spatially when OCR metadata is unavailable
export const inferPositionsSpatially = (elements) => {
  const sorted = elements
    .map((entry) => {
      const coords = parseAndFlattenPolygon(entry.coordinatesInInches);
      return { entry, x: coords[0] || 0, y: coords[1] || 0 };
    })
    .sort((a, b) => (Math.abs(a.y - b.y) > 0.05 ? a.y - b.y : a.x - b.x));

  const matrix = [];
  let currentRow = 0,
    lastY = sorted[0]?.y || 0;
  sorted.forEach((item, index) => {
    if (Math.abs(item.y - lastY) > 0.05) {
      currentRow++;
      lastY = item.y;
    }
    matrix[item.entry.id] = buildMatrixEntry(item.entry, currentRow, index);
  });
  return matrix;
};

export const triggerPopUpStatusForCopyFormula = (
  copyFormulaResult,
  elementIds,
) => {
  let elementWiseStats = [];
  let successMessageDto = [];
  let failureMessageDto = [];

  // I intend to create the below structure in elementWiseStats
  /**ElementID |	Formula Pasted |	Formula Did not pasted
   * -------------------------------------------------
        1	          1               2
        2	          3	              2
        3	          2	              1
        4	          3	              3
        5	          1	              3
 */
  elementIds.forEach((id, index) => {
    elementWiseStats.push({
      elementId: id,
      numberOfSuccessfulPaste: copyFormulaResult.filter(
        (ele) => ele.elementId === id && ele.copyFormulaStatus === COPY_SUCCESS,
      ).length,
      numberOfFailedPaste: copyFormulaResult.filter(
        (ele) => ele.elementId === id && ele.copyFormulaStatus === COPY_FAILED,
      ).length,
    });
  });

  // I intend to create the below structure in successMessageDto.
  // This is derived from elementWiseStats
  /**NumberOfElements |	 NumberOfFormulaSuccesfullyPasted
   * -------------------------------------------------
        2                       1
        2                       3
        1                       2
 */
  /** It can be interpreted as :
   * There is 2 elements for which 1 formula is succesfully pasted
   * There is 1 element for which 3 formula is successfully pasted
   * There is 1 element for which 2 formula is successfully pasted
   */
  /**The similar kind of structure is there in  failureMessageDto*/
  elementWiseStats.forEach((stats, index) => {
    /**numberOfSameSuccessElements - Number of elements for which same amount of formula is successfully pasted */
    let numberOfSameSuccessElements = elementWiseStats.filter(
      (ele) => ele.numberOfSuccessfulPaste === stats.numberOfSuccessfulPaste,
    ).length;
    /**numberOfSameFailedElements - Number of elements for which same amount of formula has been failed to paste */
    let numberOfSameFailedElements = elementWiseStats.filter(
      (ele) => ele.numberOfFailedPaste === stats.numberOfFailedPaste,
    ).length;
    if (
      successMessageDto.findIndex(
        (ele) =>
          stats.numberOfSuccessfulPaste === ele.numberOfSuccessfulPaste &&
          numberOfSameSuccessElements === ele.numberOfElements,
      ) === -1 &&
      stats.numberOfSuccessfulPaste > 0
    ) {
      successMessageDto.push({
        numberOfSuccessfulPaste: stats.numberOfSuccessfulPaste,
        numberOfElements: numberOfSameSuccessElements,
      });
    }
    if (
      failureMessageDto.findIndex(
        (ele) =>
          stats.numberOfFailedPaste === ele.numberOfFailedPaste &&
          numberOfSameFailedElements === ele.numberOfElements,
      ) === -1 &&
      stats.numberOfFailedPaste > 0
    ) {
      failureMessageDto.push({
        numberOfFailedPaste: stats.numberOfFailedPaste,
        numberOfElements: numberOfSameFailedElements,
      });
    }
  });
  return { successMessageDto, failureMessageDto };
};
